import { useId, useMemo, useState } from "react"
import { Area, AreaChart, CartesianGrid, XAxis, YAxis, LabelList } from "recharts"
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "./ui/chart"
import {
  formatDate,
  dateLabelFormatter,
  ChartGradient,
  GlowFilter,
  ChartDot,
  ActiveDot,
} from "../lib/chart-utils.jsx"

const DEFAULT_MAX_DAYS = 15

const chartConfigs = {
  count: {
    citations: {
      label: "Citations",
      color: "hsl(220, 80%, 60%)",
    },
  },
  visibility: {
    visibility: {
      label: "Visibility",
      color: "hsl(220, 80%, 60%)",
    },
  },
}

export function AiModeCitationsChart({ data, maxDays = DEFAULT_MAX_DAYS }) {
  const [mode, setMode] = useState("count")
  const uid = useId().replace(/:/g, "")
  const gradientId = `citGrad-${uid}`
  const glowId = `citGlow-${uid}`

  const { chartData, yDomain } = useMemo(() => {
    if (!data || data.length === 0) return { chartData: [], yDomain: [0, 10] }

    const displayed = maxDays ? data.slice(-maxDays) : data
    const mapped = displayed.map((d) => ({
      date: d.date,
      dateLabel: formatDate(d.date),
      citations: d.citations,
      visibility: d.visibility,
    }))

    const dataKey = mode === "count" ? "citations" : "visibility"
    const values = mapped.map((d) => d[dataKey]).filter((v) => v != null)
    const maxVal = Math.max(...values, mode === "count" ? 1 : 10)
    const yMax = mode === "count" ? maxVal + 1 : Math.min(maxVal + 10, 100)

    return { chartData: mapped, yDomain: [0, yMax] }
  }, [data, maxDays, mode])

  if (!data?.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-12 text-sm text-gray-500">
        No source data available
      </div>
    )
  }

  const dataKey = mode === "count" ? "citations" : "visibility"
  const config = chartConfigs[mode]
  const colorVar = mode === "count" ? "var(--color-citations)" : "var(--color-visibility)"

  return (
    <div className="flex flex-col">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <p className="text-sm text-gray-500">How often your domain appeared in Sources</p>
        <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
          <button
            onClick={() => setMode("count")}
            className={`px-3 py-1.5 font-medium transition-colors ${mode === "count" ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-50"}`}
          >
            Count
          </button>
          <button
            onClick={() => setMode("visibility")}
            className={`px-3 py-1.5 font-medium transition-colors border-l border-gray-200 ${mode === "visibility" ? "bg-gray-900 text-white" : "text-gray-500 hover:bg-gray-50"}`}
          >
            Visibility
          </button>
        </div>
      </div>
      <div className="h-[300px] p-6">
        <ChartContainer config={config} className="h-full w-full">
          <AreaChart
            data={chartData}
            margin={{ top: 32, right: 20, left: 0, bottom: 4 }}
            accessibilityLayer
          >
            <defs>
              <ChartGradient id={gradientId} colorVar={colorVar} />
              <GlowFilter id={glowId} />
            </defs>
            <CartesianGrid
              vertical={false}
              strokeDasharray="3 3"
              stroke="hsl(220, 15%, 91%)"
            />
            <XAxis
              dataKey="dateLabel"
              tickLine={false}
              axisLine={false}
              tickMargin={12}
              tick={{ fontSize: 12, fill: "#9ca3af" }}
            />
            <YAxis
              domain={yDomain}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              allowDecimals={false}
              width={32}
              tick={{ fontSize: 12, fill: "#9ca3af" }}
            />
            <ChartTooltip
              cursor={{ stroke: colorVar, strokeWidth: 1, strokeDasharray: "4 4" }}
              content={
                <ChartTooltipContent
                  className="backdrop-blur-md bg-white/90 border-gray-200/60 shadow-2xl rounded-xl"
                  formatter={(value) => (
                    <div className="flex items-center gap-2.5 py-0.5">
                      <div
                        className="h-3 w-3 shrink-0 rounded-full shadow-sm"
                        style={{ backgroundColor: colorVar }}
                      />
                      <span className="text-gray-500 text-[13px]">
                        {mode === "count" ? "Citations" : "Visibility"}
                      </span>
                      <span className="font-mono font-semibold tabular-nums text-gray-900 text-[13px] ml-auto">
                        {mode === "count" ? value : `${value}%`}
                      </span>
                    </div>
                  )}
                  hideLabel={false}
                  labelClassName="text-gray-700 text-[13px] pb-1 border-b border-gray-100 mb-1"
                  labelFormatter={dateLabelFormatter}
                />
              }
            />
            <Area
              type="monotone"
              dataKey={dataKey}
              stroke={colorVar}
              fill={`url(#${gradientId})`}
              strokeWidth={2.5}
              filter={`url(#${glowId})`}
              dot={(props) => <ChartDot {...props} dataKey={dataKey} colorVar={colorVar} />}
              activeDot={(props) => <ActiveDot {...props} colorVar={colorVar} />}
              animationDuration={800}
              animationEasing="ease-out"
            >
              <LabelList
                dataKey={dataKey}
                position="top"
                offset={12}
                formatter={(value) => mode === "count" ? value : `${value}%`}
                className="fill-blue-600 font-semibold text-[11px]"
              />
            </Area>
          </AreaChart>
        </ChartContainer>
      </div>
    </div>
  )
}
